<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  useColdCallModalController,
  type ColdCallRow,
} from '@/composables/ColdMonitoring/useColdMonitoringCallDetails'
import {
  OTranscript,
  useDateTime,
  type OAudioController,
  type OAudioProvider,
} from '@ai/ui-components'
import { useLanguages } from '@/composables/translations/useLanguages'
import type { VaLanguage } from 'shared'
import { usePhoneFormatter } from '@/composables/formatting/usePhoneFormatter'
import { useOTranscriptContext } from '@/composables/transcripts/useOTranscriptContext'
import { useCopyCallLink } from '@/composables/ColdMonitoring/useCopyCallLink'
import {
  RIGHT_PANEL_TOAST_VIEWPORT_PRESET_FLOATING,
  useToastViewportScope,
} from '@/composables/toast'
import { LIST_ACTION_ICONS } from '@/components/BusinessLogic/TableActions'
import CallDetailsNavigation from '@/components/BusinessLogic/CallDetails/CallDetailsNavigation.vue'
import CallDetailsMetadataCard, {
  type CallDetailsMetadataRow,
} from '@/components/BusinessLogic/CallDetails/CallDetailsMetadataCard.vue'
import { useCallDetailsRightPanelClass } from '@/components/BusinessLogic/CallDetails/useCallDetailsRightPanelClass'
import { isNotBlank } from '@/utils/String'

const { t, locale } = useI18n({ useScope: 'global' })
const { formatTimerDuration } = useDateTime()
const { languageLabel } = useLanguages()
const formatLanguage = (language: VaLanguage) => languageLabel(locale.value, language)

const props = defineProps<{
  item: ColdCallRow
  calls: ColdCallRow[]
  audioController: OAudioController
  getRecordingProvider: (callId: string, url: string) => OAudioProvider
}>()

const emit = defineEmits<(e: 'download-call' | 'navigate-call', value: string) => void>()

const controller = useColdCallModalController({
  calls: computed(() => props.calls),
  initialItem: computed(() => props.item),
  formatTimerDuration,
  audioController: props.audioController,
})
const { formatPhone } = usePhoneFormatter()
const agentLabel = computed(() => {
  const agentName = controller.currentCall.value.agent?.name
  const name = controller.currentCall.value.isLiveTest
    ? t('cold_monitoring.live_test_caller')
    : isNotBlank(agentName)
      ? agentName
      : t('deleted_agent')
  return `${t('agent')}: ${name}`
})
const summaryText = computed(() => {
  const summary = controller.summary.value?.trim()
  return isNotBlank(summary) ? summary : null
})
const callOutputText = computed(() => {
  const callOutput = controller.conversationOutput.value?.trim()
  return isNotBlank(callOutput) ? callOutput : '-'
})

const metadataCardOpenSequence = ref(0)
const metadataCardKey = computed(
  () => `${controller.currentCall.value.id}-${String(metadataCardOpenSequence.value)}`,
)
const panelClass = useCallDetailsRightPanelClass(controller.panelOpen)
const metadataRows = computed<CallDetailsMetadataRow[]>(() => [
  {
    id: 'caller-number',
    label: t('callers_number'),
    value: controller.currentCall.value.isLiveTest
      ? t('cold_monitoring.live_test_caller')
      : formatPhone(controller.currentCall.value.callerNumber),
    private: !controller.currentCall.value.isLiveTest,
  },
  {
    id: 'language',
    label: t('language'),
    value: formatLanguage(controller.currentCall.value.language),
  },
  {
    id: 'call-output',
    label: t('call_output'),
    value: callOutputText.value,
  },
  {
    id: 'call-id',
    label: t('call_id'),
    value: controller.callIdFormatted.value,
  },
  {
    id: 'call-duration',
    label: t('call_duration'),
    value: controller.callDuration.value,
  },
])

const transcriptRecordingAudio = computed(() => {
  const audio = controller.audio.value
  const url = audio?.recordingUrl ?? audio?.url ?? controller.currentCall.value.audioUrl
  if (!url) return null

  const trackId = controller.currentCall.value.id
  return {
    mode: 'controlled-recording' as const,
    // ADR: controlled-recording must reuse the exact provider instance used by the table
    // Listen action. OTranscript validates provider identity against
    // audioController.state.activeTrack.provider; same key/cacheKey/url is not enough.
    //
    // Broken refactor: create a modal-owned provider here.
    //
    // That creates a modal-owned provider instance while the controller is playing a
    // parent-owned provider instance, so OTranscript reports audio as unavailable.
    provider: props.getRecordingProvider(trackId, url),
    trackId,
    controller: props.audioController,
    cacheKey: trackId,
  }
})
const transcriptContext = useOTranscriptContext({
  transcripts: () => controller.transcript.value,
  events: () => controller.events.value,
  audio: transcriptRecordingAudio,
})
const showDetailsLoading = computed(
  () => controller.isLoading.value && !transcriptRecordingAudio.value,
)

const { getCopyLinkAction } = useCopyCallLink()

const modalActions = computed(() => [
  getCopyLinkAction(controller.currentCall.value.id),
  {
    label: t('download'),
    value: 'download' as const,
    icon: LIST_ACTION_ICONS.download,
    handler: () => {
      emit('download-call', controller.currentCall.value.id)
    },
    dataTestid: 'action-download',
  },
])
useToastViewportScope(controller.panelOpen, RIGHT_PANEL_TOAST_VIEWPORT_PRESET_FLOATING)

const openPanel = (callId: string) => {
  metadataCardOpenSequence.value++
  controller.openPanel(callId)
}

const navigateCall = (direction: 'left' | 'right') => {
  const previousCallId = controller.currentCall.value.id
  if (direction === 'left') {
    controller.prevCall()
  } else {
    controller.nextCall()
  }

  const currentCallId = controller.currentCall.value.id
  if (currentCallId !== previousCallId) {
    emit('navigate-call', currentCallId)
  }
}

defineExpose({
  openPanel,
  panelOpen: controller.panelOpen,
})
</script>

<template>
  <ORightPanel
    v-model:open="controller.panelOpen.value"
    :title="t('call_details')"
    floating
    hide-header-divider
    :panel-class="panelClass"
    header-class="pb-0 pl-6"
    content-class="-mb-6 min-h-0 overflow-hidden radius-b-xl pl-0 pr-0 w-[calc(100%+1.5rem)]"
  >
    <template #title-extra>
      <CallDetailsNavigation
        :left-disabled="
          controller.isLoading.value || controller.isDebouncing.value || !controller.hasPrev.value
        "
        :right-disabled="
          controller.isLoading.value || controller.isDebouncing.value || !controller.hasNext.value
        "
        @navigate="navigateCall"
      />
    </template>

    <template #header>
      <CallDetailsMetadataCard
        :key="metadataCardKey"
        :title="agentLabel"
        :rows="metadataRows"
        :collapsed-rows="1"
        gap-class="gap-s"
        data-testid="cold-call-details-card"
      >
        <template #actions>
          <OListActions small :actions="modalActions" data-testid="cold-call-modal-more-actions" />
        </template>
      </CallDetailsMetadataCard>
    </template>

    <template #default>
      <div
        v-if="showDetailsLoading"
        data-testid="cold-call-modal-loading"
        class="flex h-full min-h-0 items-start justify-center pt-16"
      >
        <OSpinner size="XL" />
      </div>
      <div
        v-else
        data-testid="cold-call-modal-transcript-layout"
        class="flex h-full min-h-0 flex-col overflow-hidden"
      >
        <div data-testid="call-summary-accordion" class="shrink-0 px-6 py-l">
          <OAccordion
            variant="filled"
            :title="t('call_summary')"
            :empty-text="t('no_call_summary_available')"
          >
            <template v-if="summaryText">
              <p class="typography-body-2">
                {{ summaryText }}
              </p>
            </template>
          </OAccordion>
        </div>
        <div
          data-testid="cold-call-modal-transcript-section"
          class="min-h-0 flex-1 border-t border-neutral-5"
        >
          <OTranscript
            class="h-full"
            :audio="transcriptContext.audio.value"
            :transcript="transcriptContext.transcript.value"
            :events="transcriptContext.events.value"
            :interactive="false"
            :show-events-filter="false"
          >
            <template #empty-transcript>
              <div class="max-w-60">
                <OState
                  type="empty"
                  variant="2"
                  :state="{
                    title: t('transcript_not_available'),
                    description: t('view_call_details_in_the_events_tab'),
                  }"
                />
              </div>
            </template>

            <template #empty-events>
              <div class="max-w-60">
                <OState type="empty" variant="2" />
              </div>
            </template>
          </OTranscript>
        </div>
      </div>
    </template>
  </ORightPanel>

  <slot :handle-click="openPanel" />
</template>
